import React, { useEffect, useState } from "react";
import CrmLayout from "../../components/CrmLayout";
import api, { formatApiError } from "../../lib/api";
import { money } from "../../lib/format";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";

const EMPTY = { name: "", description: "", duration_min: 60, price: 0, category: "Haircuts" };

export default function Services() {
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const load = () => api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ name: s.name, description: s.description, duration_min: s.duration_min, price: s.price, category: s.category }); setOpen(true); };
  const save = async () => {
    try {
      const p = { ...form, duration_min: Number(form.duration_min), price: Number(form.price) };
      if (editing) await api.put(`/services/${editing.id}`, p); else await api.post("/services", p);
      toast.success("Service saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => { try { await api.delete(`/services/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Delete failed"); } };

  return (
    <CrmLayout title="Services" actions={<Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md" data-testid="add-service-btn" onClick={openNew}><Plus size={16} className="mr-1" /> Add service</Button>}>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {services.map((s) => (
          <div key={s.id} className="bg-card border border-border rounded-lg p-5" data-testid={`service-card-${s.id}`}>
            <div className="flex justify-between items-start">
              <div><div className="label-eyebrow text-primary mb-1">{s.category}</div><h3 className="font-semibold">{s.name}</h3></div>
              <div className="font-display text-xl font-bold">{money(s.price)}</div>
            </div>
            <p className="text-sm text-muted-foreground mt-2 min-h-[40px]">{s.description}</p>
            <div className="flex items-center justify-between mt-3">
              <span className="text-xs text-muted-foreground">{s.duration_min} min</span>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" data-testid={`edit-service-${s.id}`} onClick={() => openEdit(s)}><Pencil size={14} /></Button>
                <Button variant="ghost" size="sm" className="text-destructive" data-testid={`delete-service-${s.id}`} onClick={() => remove(s.id)}><Trash2 size={14} /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-xl font-semibold">{editing ? "Edit" : "Add"} service</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid="service-name" className="mt-1" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></div>
            <div><Label>Description</Label><Textarea data-testid="service-desc" className="mt-1" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Duration</Label><Input data-testid="service-duration" type="number" className="mt-1" value={form.duration_min} onChange={(e) => setForm((f) => ({ ...f, duration_min: e.target.value }))} /></div>
              <div><Label>Price ($)</Label><Input data-testid="service-price" type="number" className="mt-1" value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} /></div>
              <div><Label>Category</Label><Input data-testid="service-category" className="mt-1" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} /></div>
            </div>
          </div>
          <DialogFooter><Button data-testid="service-save" onClick={save} className="bg-primary text-primary-foreground hover:bg-primary/90">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </CrmLayout>
  );
}
